import LectraAuth from "lectra-auth-js";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

const AuthenticationContext = React.createContext("Authentication");

const APP_METADATA = "https://metadata.lectra.com/app_metadata";

const wait = (timeout = 0) => {
  return new Promise(resolve => setTimeout(resolve, timeout));
};

const hasPermissionToLoadApplication = (userPermissions, permission) => {
  if (!permission) {
    return true;
  }
  return userPermissions.includes(permission);
};

const retrievePermissionsUser = (userInfos = undefined) => {
  if (userInfos && userInfos[APP_METADATA]) {
    return userInfos[APP_METADATA].permissions;
  }
  return null;
};

async function fetchAuth0(Auth, permission) {
  try {
    const response = await Auth.checkSSO();
    const userInfos = Auth.decodeToken(response.idToken);
    const userPermissions = retrievePermissionsUser(userInfos);
    const hasPermission = hasPermissionToLoadApplication(
      userPermissions,
      permission
    );

    return {
      hasPermission,
      accessToken: response.accessToken,
      idToken: response.idToken,
      user: userInfos
    };
  } catch (error) {
    await Auth.redirectToLogin();
    return {
      hasPermission: false,
      accessToken: "",
      idToken: "",
      user: {}
    };
  }
}

const AuthenticationProvider = ({
  auth0,
  permission,
  SSOHeartBeat,
  children
}) => {
  const [userState, setAuthenticated] = useState({
    accessToken: "",
    idToken: "",
    user: {},
    isAuthenticated: false
  });

  const Auth = new LectraAuth({
    ...auth0,
    callbackUrl: "/"
  });

  useEffect(() => {
    (async () => {
      const { hasPermission, accessToken, idToken, user } = await fetchAuth0(
        Auth,
        permission
      );
      if (!hasPermission) {
        Auth.redirectToLogin();
      } else {
        await setAuthenticated({
          accessToken,
          idToken,
          user,
          isAuthenticated: true
        });
        if (SSOHeartBeat) {
          await ssoLogout();
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => Auth.logout();

  const ssoLogout = async (interval = 5000) => {
    while (true) {
      await wait(interval);
      try {
        const sso = await Auth.IsSSOActive();
        if (!sso) {
          await logout();
        }
      } catch (error) {
        // We are probably offline... Do nothing...
      }
    }
  };
  return (
    <AuthenticationContext.Provider
      value={{
        ...userState,
        actions: {
          logout
        }
      }}
    >
      {userState.isAuthenticated ? children : <p>{"Authenticating"}...</p>}
    </AuthenticationContext.Provider>
  );
};

AuthenticationProvider.propTypes = {
  SSOHeartBeat: PropTypes.bool,
  auth0: PropTypes.shape({
    authDomain: PropTypes.string.isRequired,
    clientId: PropTypes.string.isRequired,
    baseUrl: PropTypes.string.isRequired,
    callbackUrl: PropTypes.string,
    silentCallbackUrl: PropTypes.string,
    audience: PropTypes.string.isRequired,
    scopes: PropTypes.string
  }),
  permission: PropTypes.string,
  children: PropTypes.node.isRequired
};

AuthenticationProvider.defaultProps = {
  SSOHeartBeat: false,
  permission: ""
};

const AuthenticationConsumer = AuthenticationContext.Consumer;

export default AuthenticationProvider;
export { AuthenticationConsumer, AuthenticationContext };
